import {
  getOnboardingBadgesForCountry,
  mapCountryCodeToSupportedCountry,
} from '@/components/modal/sign-in-modal/compliant-logos';
import {
  GET_ONBOARDING_V2_VARIANT,
  IS_PRACTICE_INVITE_FLOW_ENABLED,
  useIsOnboardingGuidedTourEnabled,
  useIsOnboardingResumeEnabled,
} from '@/features/flags';
import { TemplateEditorTab } from '@/features/onboarding-v2/components/personalization/preview/TemplateEditorTab';
import { useLocalizedSpecialtyName } from '@/features/onboarding-v2/hooks/useLocalizedSpecialtyName';
import { useOnboardingGeoCountryDefault } from '@/features/onboarding-v2/hooks/useOnboardingGeoCountryDefault';
import { useSelectedEmrOption } from '@/features/onboarding-v2/hooks/useSelectedEmrOption';
import { useSelectedSpecialtyCanonicalName } from '@/features/onboarding-v2/hooks/useSelectedSpecialtyCanonicalName';
import { useTemplateReviewState } from '@/features/onboarding-v2/hooks/useTemplateReviewState';
import { useQuerySpecialtyOnboardingV2Assets } from '@/hooks/query/useQuerySpecialtyOnboardingV2Assets';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { configureOnboardingHost, type OnboardingHost } from '@heidi/growth';
import { TemplateAssemblyLoader } from '@heidi/templates';

const buildOnboardingHost = (): OnboardingHost => ({
  isPracticeInviteFlowEnabled: () => IS_PRACTICE_INVITE_FLOW_ENABLED(),
  isAppleCompliantBuild: () => isAppleCompliantBuild(),
  isProductionBuild: () => process.env.NODE_ENV === 'production',
  useIsOnboardingGuidedTourEnabled: () => useIsOnboardingGuidedTourEnabled(),
  useIsOnboardingResumeEnabled: () => useIsOnboardingResumeEnabled(),
  useOnboardingGeoCountryDefault: (setFormData, options) =>
    useOnboardingGeoCountryDefault(setFormData, options),
  trackTourScreenViewed: (screenName) =>
    track({
      event_name: TrackingEventName.ScreenViewed,
      props: {
        screen_name: screenName,
        onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
        // The guided-tour screens render in the treatment arm only.
        onboarding_sub_variant: 'guided_tour',
      },
    }),
  useQuerySpecialtyOnboardingV2Assets: (specialtyName, locale) =>
    useQuerySpecialtyOnboardingV2Assets(specialtyName, locale),
  useSelectedSpecialtyCanonicalName: () => useSelectedSpecialtyCanonicalName(),
  useLocalizedSpecialtyName: () => useLocalizedSpecialtyName(),
  useSelectedEmrOption: (emrEhr, countryCode) =>
    useSelectedEmrOption(emrEhr, countryCode),
  useTemplateReviewState: (input) => useTemplateReviewState(input),
  TemplateEditorTab,
  TemplateAssemblyLoader,
  getOnboardingComplianceBadges: (countryCode) =>
    getOnboardingBadgesForCountry(mapCountryCodeToSupportedCountry(countryCode)),
});

configureOnboardingHost(buildOnboardingHost);
